/**
 * The Assistant: one conversation on the case, shared by everyone with it,
 * beside the workbench — a column on a wide screen, a drawer otherwise
 * (`Workbench`). It reads the case, runs the specialists within the case's
 * threshold and proposes what the analyst records; its links open the
 * workbench's views. The case agent's run statements are recorded in the
 * same conversation.
 */

import { memo, useEffect, useRef } from 'react';

import { Alert, Button, CloseButton, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';

import { useCopilot } from '@/hooks/useCopilot';
import { markCopilotParagraphs } from '@/lib/copilotHighlight';
import type { CopilotFocus, CopilotPassage } from '@/types/copilot';
import { CopilotComposer } from './CopilotComposer';
import { CopilotMessage } from './CopilotMessage';
import type { CaseLink } from './markdown';
import classes from './Copilot.module.css';

interface CopilotColumnProps {
  reportId: string;
  /** The case's change token (the header's version). */
  version: string | null;
  /** The passage selected for the next message, and its clearing. */
  passage: CopilotPassage | null;
  onPassageChange: (passage: CopilotPassage | null) => void;
  /** The item open in the workbench, offered with a message. */
  focus: CopilotFocus | null;
  onOpenLink: (link: CaseLink) => void;
  /** After an applied proposal: the case moved. */
  onChanged: () => void | Promise<void>;
  onClose: () => void;
  /** Beside the workbench (sticky), rather than in a drawer. */
  docked?: boolean;
}

const REFUSED: Record<string, string> = {
  busy: 'A reply is still running on this case',
  over_budget: 'This case is at its threshold — raise it in Needs you',
  not_in_workbench: "This case isn't in the workbench any more",
};

export const CopilotColumn = memo(
  ({
    reportId,
    version,
    passage,
    onPassageChange,
    focus,
    onOpenLink,
    onChanged,
    onClose,
    docked = false,
  }: Readonly<CopilotColumnProps>) => {
    const copilot = useCopilot(reportId, version, onChanged);
    const { conversation, messages, live } = copilot;
    const people = conversation?.people ?? {};
    const scroller = useRef<HTMLDivElement>(null);
    const pinned = useRef(true);

    // The selected passage stays marked in the document while it waits.
    const marks = passage?.paraIds.join('|') ?? '';
    useEffect(() => {
      markCopilotParagraphs(marks ? marks.split('|') : []);
      return () => markCopilotParagraphs([]);
    }, [marks]);

    // Follow the conversation down as it grows, unless scrolled up to read.
    const size = messages.reduce((n, m) => n + m.blocks.length + m.text.length, 0);
    const tail = live?.blocks.at(-1);
    const tailLength = tail?.type === 'text' ? tail.text.length : 0;
    useEffect(() => {
      const el = scroller.current;
      if (el && pinned.current) {
        el.scrollTop = el.scrollHeight;
      }
    }, [size, tailLength]);

    const running = conversation?.running ?? null;
    const busy = copilot.streaming || running !== null;
    const ours = copilot.streaming && live?.asked !== null;
    const spent = Number(conversation?.spend.spent ?? 0);
    const cap = Number(conversation?.spend.cap ?? 0);
    const scope =
      conversation?.spend.scope === 'before_start' ? 'the budget before Start' : 'the case budget';

    return (
      <Stack gap="sm" className={classes.column} data-docked={docked} data-testid="copilot-column">
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6}>
            <IconSparkles size={16} color="var(--mantine-color-violet-6)" />
            <Text fw={600} size="sm">
              Assistant
            </Text>
            {cap > 0 ? (
              <Tooltip
                label={`The case's model spend (the agents and the Assistant) against ${scope} — a threshold you raise, never a wall`}
                withArrow
              >
                <Text size="xs" c="dimmed" data-testid="copilot-spend">
                  ${spent.toFixed(2)} of ${cap.toFixed(2)}
                </Text>
              </Tooltip>
            ) : null}
          </Group>
          <Group gap={4} wrap="nowrap">
            <Button
              size="compact-xs"
              variant="subtle"
              disabled={busy || !conversation?.thread}
              onClick={() => void copilot.newConversation()}
              data-testid="copilot-new"
            >
              New conversation
            </Button>
            <CloseButton size="sm" aria-label="Close the Assistant" onClick={onClose} />
          </Group>
        </Group>
        <div
          ref={scroller}
          className={classes.messages}
          onScroll={(event) => {
            const el = event.currentTarget;
            pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          }}
        >
          <Stack gap="md">
            {copilot.error && !conversation ? (
              <Text size="sm" c="red">
                The conversation couldn&apos;t be loaded: {copilot.error.message}
              </Text>
            ) : null}
            {conversation && messages.length === 0 ? (
              <Text size="sm" c="dimmed" data-testid="copilot-empty">
                Ask about this case: what a record says and where it comes from, where to look next
                — or ask it to screen someone, research a question, record a search you ran or
                soften a passage you select in the document. It works within the case&apos;s
                threshold, and what it proposes waits for you to apply it.
              </Text>
            ) : null}
            {messages.map((message) => (
              <CopilotMessage
                key={message.id}
                message={message}
                proposals={copilot.proposals}
                people={people}
                state={message.id === (live?.messageId ?? 'replying') ? live?.state : undefined}
                onOpenLink={onOpenLink}
                onApply={copilot.apply}
                onApplyMany={copilot.applyMany}
                onDismiss={copilot.dismiss}
              />
            ))}
          </Stack>
        </div>
        {copilot.refusal ? (
          <Alert color="orange" variant="light" data-testid="copilot-refused">
            {REFUSED[copilot.refusal.reason] ?? 'Not sent'}
            {copilot.refusal.runningBy
              ? ` — ${people[copilot.refusal.runningBy] ?? copilot.refusal.runningBy}'s question is being answered`
              : ''}
            . {copilot.refusal.message}
          </Alert>
        ) : null}
        {copilot.notice ? (
          <Alert color="orange" variant="light" withCloseButton onClose={copilot.clearNotice}>
            {copilot.notice}
          </Alert>
        ) : null}
        {running && !copilot.streaming ? (
          <Text size="xs" c="dimmed">
            {people[running.by] ?? running.by} asked — the reply is running.
          </Text>
        ) : null}
        <CopilotComposer
          passage={passage}
          onPassageClear={() => onPassageChange(null)}
          focus={focus}
          busy={busy}
          canStop={busy && (ours || running !== null)}
          onStop={() => void copilot.stop()}
          onSend={copilot.send}
        />
      </Stack>
    );
  }
);
